import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LogOut,
    Coins,
    Combine,
    Image as ImageIcon,
    Scissors,
    Clapperboard,
    Video,
    ShieldCheck,
    Home as HomeIcon,
    PenLine,
    Mic2,
    Sun,
    Moon,
    PanelLeftClose,
    Menu,
    PanelLeftOpen,
    BookOpen,
    Library,
    LayoutGrid,
    Globe,
    Captions,
    UserSquare,
} from 'lucide-react';
import { getCurrentUser } from '../../services/api';
import { DASHBOARD_BLOG_BASE } from './blog/paths';
import { centsValue } from '../../utils/money';
import { Logo } from '../ui/primitives';
import { PaidBadge } from '../ui/PaidFeature';
import { useDashboardTheme } from '../../theme/DashboardThemeContext';
import { useSidebarCollapse, isAutoCollapseRoute } from '../../theme/SidebarCollapseContext';
import { clearAnalyticsUser } from '../../utils/analytics';

// Tailwind's `md` breakpoint, in JS. The rail/full decision changes what is
// RENDERED (icons alone vs. icons plus labels, and which header button), not
// just how it is styled, so a CSS media query cannot express it and the
// breakpoint has to be readable from JS.
//
// Read synchronously in the initialiser so the first paint is already correct
// — deriving it in an effect would flash the full-width panel on a phone.
const MD_BREAKPOINT = '(min-width: 768px)';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && !window.matchMedia(MD_BREAKPOINT).matches
    );

    useEffect(() => {
        const mql = window.matchMedia(MD_BREAKPOINT);
        const onChange = (e) => setIsMobile(!e.matches);
        mql.addEventListener('change', onChange);
        // Re-sync in case the width changed between the initialiser and here.
        setIsMobile(!mql.matches);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    return isMobile;
};

function Sidebar({ user: initialUser, onUserRefresh }) {
    const [user, setUser] = useState(initialUser);
    const [balance, setBalance] = useState(initialUser?.credits ?? null);
    const location = useLocation();
    const { theme, toggleTheme, vars } = useDashboardTheme();
    const { collapsed, toggleCollapsed, mobileExpanded, setMobileExpanded } = useSidebarCollapse();

    // Below md the sidebar's RESTING state is the icon rail — it used to be an
    // off-canvas drawer behind a hamburger, which meant navigation cost two
    // taps and hid the nav entirely in between.
    //
    // `collapsed` drives expansion at every size; only the presentation
    // differs. On desktop the panel widens in place and pushes the content
    // over. On mobile there is no room to do that, so the expanded panel
    // OVERLAYS instead: the wrapper in App.jsx stays a fixed w-16 below md, the
    // rail keeps its slot in the flex row, and the wide panel is absolutely
    // positioned over the content. That way expanding never reflows the page.
    const isMobile = useIsMobile();

    // Mobile expansion comes from SidebarCollapseContext, not local state: the
    // trigger lives in the main content column (a sibling of this component),
    // because below md the panel renders nothing until it is opened.

    const isRail = isMobile ? !mobileExpanded : collapsed;
    // A mobile panel currently expanded OVER the content: it needs absolute
    // positioning and a scrim, where the desktop panel just widens in place.
    const isMobileOverlay = isMobile && mobileExpanded;

    // Update local state when prop changes
    useEffect(() => {
        setUser(initialUser);
    }, [initialUser]);

    // Fetch the balance live rather than reading it off the `user` prop.
    //
    // That prop comes from the localStorage snapshot written at login and is
    // never refreshed, so it goes stale the moment anything is charged — which
    // is now every generate, export and script. Refetching on navigation keeps
    // the number honest without polling: the charge points all coincide with
    // leaving or returning to a page.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const current = await getCurrentUser();
                if (cancelled) return;
                setBalance(current?.credits ?? null);
                // Keep the snapshot other components read in step.
                localStorage.setItem('user', JSON.stringify(current));
                onUserRefresh?.(current);
            } catch (err) {
                // Non-fatal — the nav still works without the figure.
                console.error('Could not refresh balance:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [location.pathname]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Before the reload, so nothing fired in between is attributed to the user
        // who just signed out.
        clearAnalyticsUser();
        window.location.reload();
    };

    const isAdmin = Boolean(user?.is_admin);

    // Split into groups so the sidebar reads as "your workflow" vs "internal
    // tools" rather than one flat list where admin-only items are visually
    // indistinguishable from the primary navigation.
    // `children` turns an item into a section: the parent still navigates, and
    // its sub-items expand beneath it while that section is the active one.
    // Only Workflow has them — the rest are single destinations, and giving
    // them empty groups would be structure for its own sake.
    //
    // The sub-items are the same three entry points Home's cards link to, and
    // they carry the same ?tab= deep links, so the two surfaces agree on what
    // the three ways to start a video are.
    const mainNavItems = [
        { path: '/home', label: 'Home', icon: HomeIcon },
        {
            path: '/workflows',
            label: 'Workflow',
            icon: Combine,
            children: [
                { path: '/workflows?tab=topic', label: 'From Topic', tab: 'topic' },
                { path: '/workflows?tab=script', label: 'From Script', tab: 'script' },
                { path: '/workflows?tab=audio', label: 'From Audio', tab: 'audio' },
            ],
        },
        {
            // Avatar Studio is where avatars are managed (and given variants); Avatar Video uses them.
            path: '/ai-avatar/video',
            label: 'AI Avatar',
            icon: UserSquare,
            matchPrefix: '/ai-avatar',
            badge: (
                <span className="flex flex-shrink-0 items-center gap-1.5">
                    <span className="rounded-md bg-volt/15 px-1.5 py-0.5 font-mono text-[12px] font-bold text-volt">
                        🔥 Hot
                    </span>
                    {user?.ai_avatar_paid === false && <PaidBadge />}
                </span>
            ),
            children: [
                { path: '/ai-avatar/avatars', label: 'Avatar Studio' },
                { path: '/ai-avatar/video', label: 'Avatar Video' },
            ],
        },
        {
            // Assets sits under Workflow because it FEEDS it: both libraries
            // are browser-local stores shared by every video, and this is
            // where they are curated rather than used.
            //
            // The parent points at Music rather than a section index of its
            // own — a landing page listing two links the sidebar already shows
            // would be a click for nothing.
            path: '/assets/music',
            label: 'Assets',
            icon: Library,
            // Keeps the parent lit on /assets/footage too — NavLink alone only
            // matches its own `to`, which is the Music page.
            matchPrefix: '/assets/',
            children: [
                { path: '/assets/music', label: 'Music Library' },
                { path: '/assets/footage', label: 'Footage Library' },
                { path: '/assets/images', label: 'Image Library' },
            ],
        },
        { path: '/screenwriter', label: 'ScreenWriter', icon: PenLine },
        { path: '/speech-generator', label: 'Speech Generator', icon: Mic2 },
        { path: '/credits', label: 'Balance', icon: Coins },
        { path: DASHBOARD_BLOG_BASE, label: 'Blogs & Tips', icon: BookOpen },
    ];

    const adminNavItems = [
        { path: '/all-workflows', label: 'All Workflows', icon: LayoutGrid },
        { path: '/image-gen', label: 'Image Gen', icon: ImageIcon },
        { path: '/background-remover', label: 'BG Remover', icon: Scissors },
        { path: '/video-web-crawl', label: 'Video Web Crawl', icon: Globe },
        { path: '/srt-generator', label: 'SRT Generator', icon: Captions },
        { path: '/talking-head', label: 'Talking Head', icon: Video },
        { path: '/template-preview', label: 'Template Preview', icon: Clapperboard },
    ];

    const navLinkClass = ({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl py-3 transition-all duration-200 ${
            isRail ? 'justify-center px-0' : 'px-4'
        } ${
            isActive
                ? 'bg-panelHi text-bone shadow-[inset_0_0_0_1px_rgba(198,255,58,0.35)]'
                : 'text-mute hover:bg-panelHi/50 hover:text-bone'
        }`;

    // A sub-item highlights on the ?tab= value rather than the path, since all
    // three share /workflows. NavLink's own isActive only compares the
    // path and would light up all three at once.
    const WORKFLOW_TABS = ['topic', 'script', 'audio'];
    const tabParam = new URLSearchParams(location.search).get('tab');
    // Mirrors StockWorkflows' own fallback: anything unrecognised opens Topic
    // there, so the sidebar has to highlight Topic too rather than nothing.
    const activeTab = WORKFLOW_TABS.includes(tabParam) ? tabParam : 'topic';

    const renderSubItem = (parentPath, { path, label, tab }) => {
        // Two kinds of sub-item, and they cannot share one test. Workflow's
        // three are the SAME path distinguished by ?tab=, so they match on the
        // tab; Assets' two are genuinely different paths, so they match on the
        // pathname. Keying only on the tab (as this did) would leave every
        // Assets sub-item unhighlighted, since none of them carries one.
        const isCurrent = tab
            ? location.pathname === parentPath && activeTab === tab
            : location.pathname === path.split('?')[0];
        return (
            <NavLink
                key={path}
                to={path}
                className={`group relative flex items-center gap-2.5 rounded-lg py-2 pl-4 pr-3 transition-colors duration-200 ${
                    isCurrent ? 'text-bone' : 'text-muteDim hover:text-bone'
                }`}
            >
                {/* A dot rather than a second icon: sub-items are variants of
                    one destination, and three more glyphs would compete with
                    the parent's. */}
                <span
                    aria-hidden="true"
                    className={`h-1.5 w-1.5 flex-shrink-0 rounded-full transition-colors duration-200 ${
                        isCurrent ? 'bg-volt' : 'bg-mute/30 group-hover:bg-mute'
                    }`}
                />
                <span className="font-body text-[13px]">{label}</span>
            </NavLink>
        );
    };

    const renderNavItem = ({ path, label, icon: Icon, badge, children, matchPrefix }) => {
        // A section whose parent points at ONE of its children (Assets ->
        // /assets/music) would otherwise go dark the moment you open the other
        // one, since NavLink only compares its own `to`. `matchPrefix` lets
        // such a parent stay lit across the whole section.
        const prefixActive = matchPrefix && location.pathname.startsWith(matchPrefix);
        // Always expanded, not only when the section is active: the three
        // sub-items ARE the three ways to start a video, so they are the
        // sidebar's primary call to action rather than detail to reveal on
        // arrival. Hidden in the rail, where there is no room for their labels.
        const sectionOpen = !isRail && Boolean(children);

        return (
            <div key={path}>
                <NavLink
                    to={path}
                        title={isRail ? label : undefined}
                    className={({ isActive }) => navLinkClass({ isActive: isActive || prefixActive })}
                >
                    {({ isActive: linkActive }) => {
                        const isActive = linkActive || prefixActive;
                        return (
                            <>
                                <span
                                    aria-hidden="true"
                                    className={`absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-volt transition-opacity duration-200 ${
                                        isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'
                                    }`}
                                />
                                <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-volt' : 'text-mute group-hover:text-bone'}`} />
                                {!isRail && <span className="flex-1 font-body text-sm">{label}</span>}
                                {!isRail && badge}
                            </>
                        );
                    }}
                </NavLink>

                {sectionOpen && (
                    /* Indented under the parent with a hairline running down the
                       group, so the three read as belonging to it rather than as
                       three more top-level items. */
                    <div className="ml-[26px] mt-1 flex flex-col gap-0.5 border-l border-line pl-2">
                        {children.map((child) => renderSubItem(path, child))}
                    </div>
                )}
            </div>
        );
    };

    // The live balance, in the same cents the rest of the product quotes prices
    // in. Sits on the nav item so it is visible from every page — every
    // generate, export and script now spends from it, so "what do I have left"
    // shouldn't require a page visit to answer.
    const balanceBadge = balance === null ? null : (
        <span className="flex-shrink-0 rounded-md bg-volt/15 px-1.5 py-0.5 font-mono text-[12px] font-bold tabular-nums text-volt">
            {centsValue(balance)}¢
        </span>
    );

    return (
        <>
            {/* Scrim behind the expanded mobile panel. Tap anywhere on the
                content to collapse back to the rail — the panel covers most of
                a phone screen, so it needs a dismiss target that isn't the
                toggle itself. Desktop never has one: there the panel widens in
                place and nothing is covered. */}
            {isMobileOverlay && (
                <button
                    type="button"
                    aria-label="Collapse sidebar"
                    onClick={() => setMobileExpanded(false)}
                    className="fixed inset-0 z-40 cursor-default bg-ink/60 backdrop-blur-sm md:hidden"
                />
            )}

            {/* Mobile nav trigger.
                
                Floating rather than in-flow: the pages own their own headings,
                so anything placed in the content column can only sit above or
                beside the whole page, not beside its title — and an in-flow
                button at the top pushed every page's first line sideways.
                
                Top-RIGHT, not top-left: page headings are left-aligned, so the
                right edge is clear and nothing needs extra padding to avoid it.
                
                `fixed`, since its parent wrapper is w-0 on mobile — an absolute
                child would resolve against a zero-width box — and so it stays
                reachable after the content scrolls. Hidden from md, where the
                rail itself is the control.
                
                Unlike the panel, this follows the dashboard's light/dark theme:
                it floats OVER the page content, so it has to belong to the
                surface it sits on. The panel is deliberately always dark, and
                this component mounts outside the themed wrapper (see App.jsx),
                so the active theme's variables are applied inline here rather
                than inherited — that is the only reason `vars` is read at all.
                
                Filled rather than outlined: volt fill with the icon knocked out
                in --color-ink, so the control reads as the one accented thing on
                the page in both themes.
                
                The shadow is theme-conditional too: a 45%-black drop shadow is
                right on near-black but reads as dirt on a pale panel. */}
            {/* Not on the editor: the floating button sits over its header and the
                export controls, and the editor has its own back arrow. */}
            {!mobileExpanded && !isAutoCollapseRoute(location.pathname) && (
                <button
                    type="button"
                    onClick={() => setMobileExpanded(true)}
                    aria-label="Open navigation"
                    style={vars}
                    className={`fixed right-4 top-4 z-40 rounded-lg border border-volt bg-volt p-2 text-ink transition-colors hover:bg-[var(--color-volt-hover)] md:hidden ${
                        theme === 'light'
                            ? 'shadow-[0_4px_16px_rgba(0,0,0,0.12)]'
                            : 'shadow-[0_6px_20px_rgba(0,0,0,0.45)]'
                    }`}
                >
                    <Menu className="h-5 w-5" />
                </button>
            )}

            {/* The panel. On desktop it is `relative` and its width IS the
                layout — the App.jsx wrapper matches it, so widening pushes the
                content across. On mobile it is not rendered at all until
                expanded, and then goes `absolute` over the content: the wrapper
                there is w-0, so the sidebar costs the page no width and opening
                it never reflows anything. */}
            {/* Exactly ONE base width class is emitted. Listing w-16 and w-64
                together and relying on the later one to win is not order-safe:
                CSS resolves by stylesheet order, not className order, so which
                applies would depend on how Tailwind happened to emit them. */}
            <div className={`
                inset-y-0 left-0 ${isMobileOverlay ? 'absolute z-50 w-64' : 'relative z-40 hidden w-0 md:flex'}
                ${collapsed ? 'md:w-16' : 'md:w-64'}
                transition-[width] duration-300 ease-in-out
                flex-col h-full bg-inkRaised shadow-[4px_0_24px_rgba(0,0,0,0.3)] border-r border-line
            `}>
                {/* Logo Section */}
                <div className={`flex h-20 items-center overflow-hidden border-b border-line bg-panel ${
                    isRail ? 'justify-center px-0' : 'gap-2 px-6'
                }`}>
                    {/* Desktop-only path: below md the panel is only rendered
                        when expanded, so isRail is never true there and the
                        rail's expand control is the floating button above. */}
                    {isRail ? (
                        <button
                            type="button"
                            onClick={toggleCollapsed}
                            aria-label="Expand sidebar"
                            title="Expand sidebar"
                            className="rounded-lg p-2 text-mute transition-colors hover:bg-panelHi hover:text-bone"
                        >
                            <PanelLeftOpen className="h-5 w-5" />
                        </button>
                    ) : (
                        <>
                            <div className="min-w-0 flex-1">
                                <Logo imgClassName="h-9 w-9" textClassName="text-xl" />
                            </div>
                            {/* Shown at every size: on mobile this is how the
                                expanded panel is dismissed without reaching for
                                the scrim behind it. */}
                            <button
                                type="button"
                                onClick={isMobile ? () => setMobileExpanded(false) : toggleCollapsed}
                                aria-label="Collapse sidebar"
                                title="Collapse sidebar"
                                className="flex-shrink-0 rounded-lg p-2 text-mute transition-colors hover:bg-panelHi hover:text-bone"
                            >
                                <PanelLeftClose className="h-5 w-5" />
                            </button>
                        </>
                    )}
                </div>

                {/* Navigation */}
                <nav className={`scrollbar-thin flex-1 overflow-y-auto py-6 ${isRail ? 'px-2' : 'px-4'}`}>
                    <div className="space-y-1.5">
                        {mainNavItems.map((item) =>
                            renderNavItem(
                                item.path === '/credits' ? { ...item, badge: balanceBadge } : item
                            )
                        )}
                        {/* Theme toggle sits directly under Balance — same row
                            shape as a nav item. Only the dashboard's main
                            content area (not this sidebar) actually responds
                            to the toggle; see App.jsx for why that scope is
                            deliberately excluded here. */}
                        <button
                            type="button"
                            onClick={toggleTheme}
                            title={isRail ? (theme === 'light' ? 'Dark mode' : 'Light mode') : undefined}
                            className={`group flex w-full items-center gap-3 rounded-xl py-3 text-mute transition-all duration-200 hover:bg-panelHi/50 hover:text-bone ${
                                isRail ? 'justify-center px-0' : 'px-4'
                            }`}
                        >
                            {theme === 'light' ? (
                                <Moon className="h-5 w-5 flex-shrink-0 text-mute group-hover:text-bone" />
                            ) : (
                                <Sun className="h-5 w-5 flex-shrink-0 text-mute group-hover:text-bone" />
                            )}
                            {!isRail && (
                                <span className="flex-1 text-left font-body text-sm">
                                    {theme === 'light' ? 'Dark mode' : 'Light mode'}
                                </span>
                            )}
                        </button>
                    </div>

                    {isAdmin && (
                        <div className="mt-6">
                            <div className={`mb-2 flex items-center gap-2 ${isRail ? 'justify-center px-0' : 'px-4'}`}>
                                <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 text-signal" title={isRail ? 'Admin Tools' : undefined} />
                                {!isRail && (
                                    <>
                                        <span className="font-mono text-micro text-signal">Admin Tools</span>
                                        <span aria-hidden="true" className="h-px flex-1 bg-white/10" />
                                    </>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                {adminNavItems.map(renderNavItem)}
                            </div>
                        </div>
                    )}
                </nav>

                {/* User Info & Logout — condensed to one row, still styled to
                    blend with the nav list above (no boxed panel or red
                    button) while pinned to the bottom via the flex-col
                    layout on the sidebar root. */}
                <div className={`flex items-center gap-2 border-t border-line py-3 ${
                    isRail ? 'justify-center px-0' : 'px-4'
                }`}>
                    {!isRail && (
                        <>
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-panelHi text-xs font-bold text-volt">
                                {user?.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-bone">{user?.name || 'User'}</p>
                                <p className="truncate text-xs text-mute">{user?.email}</p>
                            </div>
                        </>
                    )}
                    {/* Logout — icon-only to keep the row condensed; red
                        reserved for hover so it still reads as higher-stakes
                        without a boxed callout. */}
                    <button
                        onClick={handleLogout}
                        aria-label="Logout"
                        title="Logout"
                        className="group flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-mute transition-all duration-200 hover:bg-panelHi/50 hover:text-red-400"
                    >
                        <LogOut className="h-4 w-4 transition-transform group-hover:rotate-12" />
                    </button>
                </div>
            </div>
        </>
    );
}

export default Sidebar;
